import { downloadBlob } from '@mlm/ui/lib/download';
import {
  localYmdHm,
  yyyymmdd,
  type ExportColumn,
  type ExportTable,
} from '@mlm/ui/lib/export-table';
import {
  fetchFundsRegister,
  fetchFundsRegisterCsvBlob,
  fetchReport,
  fetchReportCsvBlob,
  type ReportColumn,
  type ReportParams,
  type ReportResult,
} from './api';

/**
 * Admin report exports — CSV, Excel (.xlsx) and PDF from ONE builder.
 *
 * Every admin report surface serves the same `{ columns, rows, summary }`
 * envelope, so the three formats are generated from that envelope rather than
 * from the DOM: nothing here scrapes a table, and there is no server PDF. CSV
 * still comes off the server (`?format=csv`) so a tenant's existing CSV files
 * are byte-identical to what they were; Excel and PDF are built in the browser
 * by `@mlm/ui/lib/export-xlsx` / `export-pdf`, both `import()`-ed on click so
 * jsPDF and the zip writer never touch the admin's initial bundle.
 *
 * These are ADVISORY surfaces: the payload already reached the browser under
 * the page's own read perm. The `export.csv` / `export.excel` / `export.pdf`
 * codes hide affordances; they are not a security boundary.
 *
 * Pure except for `downloadReport` / `downloadFundsRegister`, which do the I/O.
 */

export type ReportExportFormat = 'csv' | 'xlsx' | 'pdf';

/** jsPDF's built-in Helvetica has no peso glyph — say the currency instead. */
export const AMOUNTS_NOTE = 'Amounts in PHP';

/**
 * Filename-safe slug for the admin exports.
 *
 * Deliberately NOT `slugify` from `@mlm/ui/lib/export-table`: that one is
 * consignment-flavoured — it falls back to the literal `'consignee'` for a
 * name that slugs to nothing, which would be nonsense on a Trial Balance.
 */
export function reportSlug(value: string): string {
  const out = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
  return out || 'report';
}

/** `report-<slug>-<yyyymmdd>` — the admin counterpart of the cashier's
 *  `consignee-<slug>-<kind>-<date>`. */
export function reportFilenameBase(name: string, generatedAt: Date, suffix?: string): string {
  return `report-${reportSlug(name)}-${suffix ?? yyyymmdd(generatedAt)}`;
}

/**
 * A report's columns as the export writers want them.
 *
 * `type` is what makes an Excel cell a real, summable number rather than text.
 * Most of the ~40 existing reports don't declare one, so an untyped column is
 * INFERRED from its alignment: the reports right-align exactly their numeric /
 * money columns, which makes `align: 'right'` a reliable "this is a number"
 * signal. `number` (not `money`) is the safe inference — it keeps the value
 * numeric without asserting a currency format over counts like PV or qty. A
 * report that wants money/date semantics declares `type` server-side.
 */
export function reportExportColumns(columns: ReportColumn[]): ExportColumn[] {
  return columns.map((c) => ({
    header: c.label,
    key: c.key,
    type: c.type ?? (c.align === 'right' ? 'number' : 'string'),
  }));
}

export type ReportExportMeta = {
  /** Document heading — also the default sheet name and filename stem. */
  title: string;
  /** Context lines under the heading (period, account, branch…). */
  subtitle?: string[];
  sheetName?: string;
  filenameBase?: string;
  /** Fixed in tests; defaults to now at click time. */
  generatedAt?: Date;
};

/**
 * `ReportResult` + meta → the shared `ExportTable`.
 *
 * The `summary` strip becomes SUBTITLE lines rather than footer rows: its
 * entries are free-form `{label, value}` pairs (Opening balance, In balance,
 * Total records…) that do not line up with the table's columns, so pinning them
 * under a column would invent a total that isn't there. As subtitles they land
 * in the PDF header and on the workbook's Info sheet, where they read as the
 * context they are.
 */
export function reportToExportTable(result: ReportResult, meta: ReportExportMeta): ExportTable {
  const generatedAt = meta.generatedAt ?? new Date();
  return {
    title: meta.title,
    subtitle: [
      ...(meta.subtitle ?? []),
      ...(result.summary ?? []).map((s) => `${s.label}: ${s.value}`),
      AMOUNTS_NOTE,
      `Generated ${localYmdHm(generatedAt)}`,
    ],
    sheetName: meta.sheetName ?? meta.title,
    columns: reportExportColumns(result.columns),
    // Rows carry keys outside `columns` (e.g. the sales reports' `order_id`);
    // the writers only read the column keys, so they are simply ignored.
    rows: result.rows,
    filenameBase: meta.filenameBase ?? reportFilenameBase(meta.title, generatedAt),
  };
}

/** Hand an already-built table to the right (lazily loaded) writer. */
async function emitTable(table: ExportTable, format: 'xlsx' | 'pdf'): Promise<void> {
  if (format === 'xlsx') {
    (await import('@mlm/ui/lib/export-xlsx')).downloadTableXlsx(table);
  } else {
    (await import('@mlm/ui/lib/export-pdf')).downloadTablePdf(table);
  }
}

/**
 * The generalized admin download path: one report key, one param set, any of
 * the three formats.
 *
 * Pass `result` when the caller already has the payload on screen — the file
 * then matches the rendered table exactly and costs no extra request. Without
 * it the report is refetched, which is what a surface with no loaded table
 * (or a stale one) wants.
 */
export async function downloadReport(
  key: string,
  params: ReportParams,
  format: ReportExportFormat,
  meta: ReportExportMeta & { result?: ReportResult },
): Promise<void> {
  const generatedAt = meta.generatedAt ?? new Date();
  const filenameBase = meta.filenameBase ?? reportFilenameBase(meta.title, generatedAt);
  if (format === 'csv') {
    downloadBlob(
      await fetchReportCsvBlob(key, params),
      `${filenameBase}.csv`,
      'text/csv;charset=utf-8',
    );
    return;
  }
  const result = meta.result ?? (await fetchReport(key, params));
  await emitTable(reportToExportTable(result, { ...meta, generatedAt, filenameBase }), format);
}

/**
 * Same, for the Cash & Bank account register — a `ReportResult` served by its
 * own route (`/admin/funds-accounts/:id/register`) rather than the reports
 * registry, so it needs its own transport but shares every builder.
 */
export async function downloadFundsRegister(
  accountId: string,
  params: { from?: string; to?: string },
  format: ReportExportFormat,
  meta: ReportExportMeta & { result?: ReportResult },
): Promise<void> {
  const generatedAt = meta.generatedAt ?? new Date();
  const filenameBase = meta.filenameBase ?? reportFilenameBase(meta.title, generatedAt);
  if (format === 'csv') {
    downloadBlob(
      await fetchFundsRegisterCsvBlob(accountId, params),
      `${filenameBase}.csv`,
      'text/csv;charset=utf-8',
    );
    return;
  }
  const result = meta.result ?? (await fetchFundsRegister(accountId, params));
  await emitTable(reportToExportTable(result, { ...meta, generatedAt, filenameBase }), format);
}
